import { create } from 'zustand';
import type { AuthUser } from '../types/api.types';
import { useTenantStore } from './tenant.store';
import { decodeJwt } from '../utils/jwt';

// ---------------------------------------------------------------------------
// Auth store — Zustand
// ---------------------------------------------------------------------------
// Fixes applied (2026-06-08):
//  - `isAuthenticated` was a getter reading localStorage on every call but was
//    not reactive (Zustand doesn't track plain getters).  Now it is a proper
//    boolean field that is updated via set().
//  - Removed duplicate `login` / `setSession` (kept `setSession`, aliased for
//    backward-compat with existing call sites).
//  - `logout` now also clears token/user from the state slice so derived
//    selectors react immediately.
// ---------------------------------------------------------------------------

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** Token del SuperAdmin original (cuando está impersonando un tenant) */
  superAdminToken: string | null;
  superAdminUser: AuthUser | null;
  /** Persist token + user and mark as authenticated */
  setSession: (token: string, user: AuthUser) => void;
  /** Alias of setSession — kept for backward-compat */
  login: (token: string, user: AuthUser) => void;
  /** Entra al dashboard de un tenant como SuperAdmin */
  impersonate: (token: string, user: AuthUser) => void;
  /** Sale de la impersonación y restaura la sesión original del SuperAdmin */
  exitImpersonation: () => void;
  logout: () => void;
}

// R-23: Se usa sessionStorage en lugar de localStorage para reducir la
// ventana de exposición del token (no persiste entre sesiones del navegador).
// Roadmap: migrar a HttpOnly cookie (Set-Cookie en /api/auth/verify-otp,
// withCredentials en axios) cuando se coordine con el backend.
const TOKEN_KEY            = 'agrovid_token';
const USER_KEY             = 'agrovid_user';
const SA_TOKEN_KEY         = 'agrovid_sa_token';
const SA_USER_KEY          = 'agrovid_sa_user';

const storage = typeof window !== 'undefined' ? sessionStorage : null;

function readToken(): string | null {
  try { return storage?.getItem(TOKEN_KEY) ?? null; }
  catch { return null; }
}

function readUser(): AuthUser | null {
  try { return JSON.parse(storage?.getItem(USER_KEY) ?? 'null'); }
  catch { return null; }
}

function readSaToken(): string | null {
  try { return storage?.getItem(SA_TOKEN_KEY) ?? null; }
  catch { return null; }
}

function readSaUser(): AuthUser | null {
  try { return JSON.parse(storage?.getItem(SA_USER_KEY) ?? 'null'); }
  catch { return null; }
}

export const useAuthStore = create<AuthState>((set, get) => {
  const token = readToken();
  const user  = readUser();

  const setSession = (token: string, user: AuthUser) => {
    try {
      storage?.setItem(TOKEN_KEY, token);
      // Decode JWT to extract email as fallback (in case it's missing from response)
      const decoded = decodeJwt(token);
      const userWithEmail = {
        ...user,
        email: user.email || decoded?.email,
      };
      storage?.setItem(USER_KEY, JSON.stringify(userWithEmail));
      set({ token, user: userWithEmail, isAuthenticated: true });
    } catch { /* private mode / storage full */ }
  };

  return {
    token,
    user,
    isAuthenticated: !!token,
    superAdminToken: readSaToken(),
    superAdminUser:  readSaUser(),

    setSession,
    login: setSession,   // alias — same implementation

    /** Guarda el token actual como superAdmin y activa el token del tenant */
    impersonate: (newToken: string, tenantUser: AuthUser) => {
      const { token: currentToken, user: currentUser } = get();
      try {
        // Guarda el token original del SuperAdmin
        if (currentToken) storage?.setItem(SA_TOKEN_KEY, currentToken);
        if (currentUser)  storage?.setItem(SA_USER_KEY, JSON.stringify(currentUser));
        // Activa el token del tenant — extraer email del JWT como fallback
        const decoded = decodeJwt(newToken);
        const userWithEmail = {
          ...tenantUser,
          email: tenantUser.email || decoded?.email,
        };
        storage?.setItem(TOKEN_KEY, newToken);
        storage?.setItem(USER_KEY, JSON.stringify(userWithEmail));
      } catch { /* ignore */ }
      set({
        superAdminToken: currentToken,
        superAdminUser:  currentUser,
        token: newToken,
        user:  { ...tenantUser, email: tenantUser.email || decodeJwt(newToken)?.email },
        isAuthenticated: true,
      });
    },

    /** Restaura la sesión original del SuperAdmin */
    exitImpersonation: () => {
      const { superAdminToken, superAdminUser } = get();
      try {
        storage?.removeItem(SA_TOKEN_KEY);
        storage?.removeItem(SA_USER_KEY);
        if (superAdminToken) storage?.setItem(TOKEN_KEY, superAdminToken);
        if (superAdminUser)  storage?.setItem(USER_KEY, JSON.stringify(superAdminUser));
      } catch { /* ignore */ }
      set({
        token: superAdminToken,
        user:  superAdminUser,
        isAuthenticated: !!superAdminToken,
        superAdminToken: null,
        superAdminUser:  null,
      });
    },

    logout: () => {
      try {
        storage?.removeItem(TOKEN_KEY);
        storage?.removeItem(USER_KEY);
        storage?.removeItem(SA_TOKEN_KEY);
        storage?.removeItem(SA_USER_KEY);
      } catch { /* ignore */ }
      useTenantStore.getState().clearTenant();
      set({ token: null, user: null, isAuthenticated: false, superAdminToken: null, superAdminUser: null });
    },
  };
});
